import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff, Globe } from 'lucide-react'
import Logo from '../Logo/Logo'
// @ts-ignore
import LightRays from '../LightRays.jsx'
import supabase from '../../utils/supabase'
import {
  formatRefreshFailureReason,
  translateAuthCaughtError,
  translateAuthResponseBody,
  translateSupabaseAuthError,
} from '../../utils/authI18nErrors'
import { markRegistrationSuccessForNextView, clearRegistrationSuccessNotice } from '../../utils/registrationSuccessNotice'
import { getPasswordResetURL } from '../../utils/url'
import TermsOfServiceModal from './TermsOfServiceModal'
import { useProfileSettingsStore } from '../../stores/profileSettingsStore'

/** E.164-style sign-up: optional leading +, up to 13 digits (max stored length 14). */
const MAX_SIGNUP_PHONE_DIGITS = 13

function sanitizeSignUpPhoneInput(raw: string): string {
  const leadingPlus = raw.startsWith('+')
  const digits = raw.replace(/\D/g, '').slice(0, MAX_SIGNUP_PHONE_DIGITS)
  return leadingPlus ? `+${digits}` : digits
}

const AuthComponent: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { acceptTerms } = useProfileSettingsStore()
  const [authMode, setAuthMode] = useState<'sign_in' | 'sign_up' | 'forgot_password'>('sign_in')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [hasReadTerms, setHasReadTerms] = useState(false)
  const [showTermsModal, setShowTermsModal] = useState(false)
  const [showLangDropdown, setShowLangDropdown] = useState(false)
  // Check if current language is RTL
  const isRTL = i18n.language === 'he' || i18n.language === 'ar'

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setMessage('')

    if (password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'))
      setLoading(false)
      return
    }

    if (password.length < 6) {
      setError(t('auth.passwordTooShort'))
      setLoading(false)
      return
    }

    if (!termsAccepted) {
      setError(t('auth.mustAcceptTerms'))
      setLoading(false)
      return
    }

    try {
      // Step 1: Register directly with Backend
      console.log('[AuthComponent] Step 1: Registering with backend...')
      const w: any = window as any
      const baseUrl = w._BACKEND_BASE_URL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001'

      const res = await fetch(`${baseUrl}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          phone
        }),
      })

      if (!res.ok) {
        const errorText = await res.text()
        setError(translateAuthResponseBody(errorText || res.statusText, t))
        setLoading(false)
        return
      }

      const backendData = await res.json()
      console.log('[AuthComponent] Registration successful, response:', backendData)

      // Step 2: Login Success Handler (Same as SignIn)
      if (!backendData?.access_token) {
        setError(t('auth.errors.registrationNoToken'))
        setLoading(false)
        return
      }

      // Step 3: Save backend token to localStorage
      try {
        localStorage.setItem('backend_access_token', backendData.access_token)

        // Before Supabase sign-in: session will unmount this screen — PendingAccess reads this flag for the green banner.
        markRegistrationSuccessForNextView()

        // Also set session in Supabase client manually to keep it in sync if needed?
        // Actually, backend returns a full user object but not necessarily a Supabase session.
        // If we want Supabase client to work on frontend (RLS), we might need to signInWithPassword using the credentials
        // OR we just rely on backend token for backend calls.

        // HOWEVER: The app likely uses supabase client for some things.
        // Let's try to sign in to supabase silently to get the session established client-side too?
        // But we just created the user.
        // Let's keep it simple: We have the backend token which is most important for this app.
        // But if `useAuth` relies on Supabase session...

        // Let's do a quick signInWithPassword to Supabase to establish the session there too
        // This ensures `useAuth` (if it relies on supabase) works correctly.
        const { error: supabaseError } = await supabase.auth.signInWithPassword({
          email,
          password
        });

        if (supabaseError) {
          clearRegistrationSuccessNotice()
          console.warn('[AuthComponent] Supabase client login failed after registration:', supabaseError);
          // proceed anyway as we have backend token?
        }

      } catch (storageError) {
        console.error('[AuthComponent] ✗ localStorage.setItem failed:', storageError)
        setError(t('auth.errors.saveTokenFailed'))
        setLoading(false)
        return
      }

      // Accept terms immediately after registration so the user doesn't see the gate on first Start Call
      try {
        await acceptTerms();
      } catch {
        // Non-fatal: user will be prompted on first Start Call if this fails
      }

      // Step 4: Notify AuthContext
      window.dispatchEvent(new Event('storage'))
      window.dispatchEvent(new CustomEvent('backend-token-updated', {
        detail: { role: backendData.user?.role, center_id: backendData.user?.center_id }
      }))

      setMessage(t('auth.registrationSuccess'))
      // Assuming AuthProvider or other logic will pick up the token/session and redirect, or we do it:
      // navigate is not available here? Ah, it is not imported. 
      // The component seems to rely on parent or state change.
      // But `handleSignIn` didn't navigate explicitly.

    } catch (err: any) {
      console.error('[AuthComponent] ✗ Registration error:', err)
      setError(translateAuthCaughtError(err, t))
    } finally {
      setLoading(false)
    }
  }

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setMessage('')

    try {
      // Step 1: Authenticate with Supabase
      console.log('[AuthComponent] Step 1: Authenticating with Supabase...')
      const { data: supabaseData, error: supabaseError } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (supabaseError || !supabaseData?.user) {
        console.error('[AuthComponent] Supabase authentication failed:', supabaseError?.message)
        setError(translateSupabaseAuthError(supabaseError, t))
        setLoading(false)
        return
      }

      console.log('[AuthComponent] ✓ Supabase authentication successful for user:', supabaseData.user.id)

      // Step 2: Authenticate with backend to get JWT token.
      // Prefer refresh-by-supabase-token to avoid password-sync timing issues after recovery.
      console.log('[AuthComponent] Step 2: Authenticating with backend to get JWT token...')
      const w: any = window as any
      const baseUrl = w._BACKEND_BASE_URL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001'
      let backendData: any = null
      let refreshErrorMessage = ''

      if (supabaseData.session?.access_token?.trim()) {
        try {
          const refreshRes = await fetch(`${baseUrl}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: supabaseData.session.access_token }),
            credentials: 'include',
          })

          if (refreshRes.ok) {
            const refreshData = await refreshRes.json()
            if (refreshData?.access_token) {
              backendData = refreshData
              console.log('[AuthComponent] ✓ Backend token minted via /auth/refresh')
            } else {
              refreshErrorMessage = 'Refresh succeeded but returned no backend token.'
            }
          } else {
            refreshErrorMessage = await refreshRes.text()
            console.warn('[AuthComponent] /auth/refresh failed, falling back to /auth/login:', refreshRes.status, refreshErrorMessage)
          }
        } catch (refreshErr: any) {
          refreshErrorMessage =
            refreshErr?.message != null && String(refreshErr.message).trim() !== ''
              ? String(refreshErr.message)
              : 'Unknown refresh error'
          console.warn('[AuthComponent] /auth/refresh error, falling back to /auth/login:', refreshErrorMessage)
        }
      }

      if (!backendData?.access_token) {
        const loginRes = await fetch(`${baseUrl}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
          credentials: 'include',
        })

        if (!loginRes.ok) {
          const loginErrorText = await loginRes.text()
          console.error('[AuthComponent] ✗ Backend login failed:', loginRes.status, loginErrorText)
          // Do NOT call supabase.auth.signOut() here — the Supabase session is valid.
          // Signing out would kick the user out even though Supabase auth succeeded.
          const refreshPart = refreshErrorMessage
            ? t('auth.errors.refreshAttemptFailedPrefix', {
                reason: formatRefreshFailureReason(refreshErrorMessage, t),
              })
            : ''
          const detail = translateAuthResponseBody(loginErrorText || loginRes.statusText, t)
          setError(refreshPart + t('auth.errors.backendAuthFailed', { detail }))
          setLoading(false)
          return
        }

        backendData = await loginRes.json()
      }

      console.log('[AuthComponent] Backend login response:', backendData)
      console.log('[AuthComponent] Response keys:', Object.keys(backendData || {}))
      console.log('[AuthComponent] access_token present:', !!backendData?.access_token)
      console.log('[AuthComponent] access_token type:', typeof backendData?.access_token)

      if (!backendData?.access_token) {
        console.error('[AuthComponent] ✗ Backend login response missing access_token:', backendData)
        setError(t('auth.errors.backendNoToken'))
        setLoading(false)
        return
      }

      // Step 3: Save backend token to localStorage - THIS IS CRITICAL
      console.log('[AuthComponent] Step 3: Saving backend token to localStorage...')
      console.log('[AuthComponent] Token to save (first 50 chars):', backendData.access_token.substring(0, 50) + '...')

      try {
        localStorage.setItem('backend_access_token', backendData.access_token)
        console.log('[AuthComponent] ✓ Backend token saved successfully')

        // Verify it was actually saved
        const savedToken = localStorage.getItem('backend_access_token')
        console.log('[AuthComponent] Verification - Token in localStorage:', savedToken ? 'YES (length: ' + savedToken.length + ')' : 'NO')

        if (!savedToken) {
          console.error('[AuthComponent] ✗ CRITICAL: Token was not saved to localStorage!')
          setError(t('auth.errors.saveTokenTryAgain'))
          setLoading(false)
          return
        }
      } catch (storageError) {
        console.error('[AuthComponent] ✗ localStorage.setItem failed:', storageError)
        setError(t('auth.errors.saveTokenStorageError'))
        setLoading(false)
        return
      }

      console.log('[AuthComponent] Token details - Role:', backendData.user?.role, 'Center ID:', backendData.user?.center_id)

      // Step 4: Notify AuthContext of token change
      window.dispatchEvent(new Event('storage'))
      window.dispatchEvent(new CustomEvent('backend-token-updated', {
        detail: { role: backendData.user?.role, center_id: backendData.user?.center_id }
      }))

      console.log('[AuthComponent] ✓ Login completed successfully with backend token')
      setMessage(t('auth.loginSuccessful'))

    } catch (err: any) {
      console.error('[AuthComponent] ✗ Login error:', err)
      setError(translateAuthCaughtError(err, t))
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setMessage('')

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: getPasswordResetURL(),
      })

      if (error) {
        setError(translateSupabaseAuthError(error, t))
      } else {
        setMessage(t('auth.passwordResetEmailSent'))
      }
    } catch (err) {
      setError(translateAuthCaughtError(err, t))
    } finally {
      setLoading(false)
    }
  }

  // Social sign-in handlers are currently unused; removed to satisfy TS build

  // Get accent color from CSS variables
  const getAccentColor = () => {
    const root = document.documentElement
    const accentHsl = getComputedStyle(root).getPropertyValue('--accent').trim()

    // In dark mode, use a brighter, more visible color for light rays
    if (root.classList.contains('dark')) {
      return '#60A5FA' // Bright blue for dark mode
    }

    // Convert HSL to hex for LightRays component in light mode
    if (accentHsl) {
      const [h, s, l] = accentHsl.split(' ').map(v => parseFloat(v))
      return hslToHex(h, s, l)
    }
    return '#3A86FF' // fallback to UI color
  }

  const hslToHex = (h: number, s: number, l: number) => {
    l /= 100
    const a = s * Math.min(l, 1 - l) / 100
    const f = (n: number) => {
      const k = (n + h / 30) % 12
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
      return Math.round(255 * color).toString(16).padStart(2, '0')
    }
    return `#${f(0)}${f(8)}${f(4)}`
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900 font-inter relative overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* LightRays Background */}
      <div className="absolute inset-0 w-full h-full">
        <LightRays
          raysOrigin="bottom-left"
          raysColor={getAccentColor()}
          raysSpeed={1.2}
          rayLength={1}
          mouseInfluence={0}
          lightSpread={1}
          pulsating={false}
          fadeDistance={1.0}
          saturation={1.0}
          followMouse={true}
          noiseAmount={0.0}
          distortion={0.0}
          className="w-full h-full"
        />
      </div>

      {/* Language Switcher */}
      <div className="absolute top-4 right-4 z-20">
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowLangDropdown(prev => !prev)}
            className="flex items-center justify-center w-9 h-9 bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm border border-neutral-300 dark:border-neutral-600 text-neutral-600 dark:text-neutral-300 rounded-lg shadow-sm hover:bg-white dark:hover:bg-neutral-800 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Select language"
          >
            <Globe className="h-5 w-5" />
          </button>
          {showLangDropdown && (
            <div className={`absolute mt-1 w-36 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg overflow-hidden ${isRTL ? 'left-0' : 'right-0'}`}>
              {([
                { value: 'he', label: 'עברית' },
                { value: 'en', label: 'English' },
                { value: 'ru', label: 'Русский' },
                { value: 'ar', label: 'العربية' },
              ] as const).map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => {
                    i18n.changeLanguage(value);
                    localStorage.setItem('language', value);
                    document.documentElement.dir = (value === 'he' || value === 'ar') ? 'rtl' : 'ltr';
                    document.documentElement.lang = value;
                    setShowLangDropdown(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-700 ${i18n.language === value ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-neutral-700 dark:text-neutral-300'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Auth Form Content */}
      <div className="relative z-10 max-w-md w-full space-y-4 p-6 shadow-lg bg-white dark:bg-neutral-900 rounded-lg">
        {/* Logo and Title */}
        <div className="text-center">
          <div className="flex justify-center mb-3">
            <Logo size="sm" padding='md' />
          </div>
          <div className="mb-2">
            <h3 className="text-xl font-semibold text-neutral-800 dark:text-neutral-200 mb-1">
              {authMode === 'sign_in' ? t('auth.login') :
                authMode === 'sign_up' ? t('auth.register') :
                  t('auth.resetPassword')}
            </h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              {authMode === 'forgot_password' ? t('auth.forgotPasswordSubtitle') : t('auth.subtitle')}
            </p>
          </div>
        </div>

        {/* Error and Success Messages */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {message && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <p className="text-sm text-green-600 dark:text-green-400">{message}</p>
          </div>
        )}

        {/* Auth Forms */}
        <div className="mt-8">
          {/* Sign In Form */}
          {authMode === 'sign_in' && (
            <form onSubmit={handleSignIn} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  {t('auth.email')}
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  required
                  dir="ltr"
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth.passwordPlaceholder')}
                    required
                    dir="ltr"
                    className="w-full px-3 py-2 pr-11 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 left-0 flex w-11 items-center justify-center text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 cursor-pointer"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <Eye className="h-5 w-5" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              >
                {loading ? t('auth.signingIn') : t('auth.signIn')}
              </button>
            </form>
          )}

          {/* Sign Up Form */}
          {authMode === 'sign_up' && (
            <form onSubmit={handleSignUp} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  {t('auth.email')}
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  required
                  dir="ltr"
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  {t('auth.phone')}
                </label>
                <input
                  id="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  maxLength={MAX_SIGNUP_PHONE_DIGITS + 1}
                  value={phone}
                  onChange={(e) => setPhone(sanitizeSignUpPhoneInput(e.target.value))}
                  placeholder={t('auth.phonePlaceholder')}
                  required
                  dir="ltr"
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth.passwordPlaceholder')}
                    required
                    dir="ltr"
                    className="w-full px-3 py-2 pr-11 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 left-0 flex w-11 items-center justify-center text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 cursor-pointer"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <Eye className="h-5 w-5" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  {t('auth.confirmPassword')}
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('auth.confirmPasswordPlaceholder')}
                    required
                    dir="ltr"
                    className="w-full px-3 py-2 pr-11 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 left-0 flex w-11 items-center justify-center text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 cursor-pointer"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <Eye className="h-5 w-5" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2" dir={isRTL ? 'ltr' : 'rtl'}>
                <div className="relative flex items-center group">
                  <input
                    id="terms-checkbox"
                    type="checkbox"
                    checked={termsAccepted}
                    disabled={!hasReadTerms}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-neutral-300 dark:border-neutral-600 text-blue-600 focus:ring-blue-500 focus:ring-2 cursor-pointer
                             disabled:cursor-not-allowed disabled:opacity-50"
                    aria-describedby="terms-description"
                  />
                  {/* Custom Tooltip */}
                  {!hasReadTerms && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 text-xs font-medium text-white bg-gray-900 dark:bg-gray-700 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 shadow-sm">
                      {t('auth.readTermsTooltip', 'Please read and approve the Terms of Service first')}
                      {/* Tooltip Arrow */}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                    </div>
                  )}
                </div>
                <label htmlFor="terms-checkbox" id="terms-description" className="text-sm text-neutral-600 dark:text-neutral-400" dir={isRTL ? 'rtl' : 'ltr'}>
                  {t('auth.acceptTerms')}{' '}
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 underline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                  >
                    {t('auth.termsLink')}
                  </button>
                </label>
              </div>

              <button
                type="submit"
                disabled={loading || !termsAccepted}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              >
                {loading ? t('auth.signingUp') : t('auth.signUp')}
              </button>
            </form>
          )}

          {/* Forgot Password Form */}
          {authMode === 'forgot_password' && (
            <form onSubmit={handleForgotPassword} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  {t('auth.email')}
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  required
                  dir="ltr"
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <button
                type="submit"
                disabled={loading}

                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              >
                {loading ? t('auth.sendingResetEmail') : t('auth.sendResetEmail')}
              </button>
            </form>
          )}

          {/* Navigation Links */}
          <div className="mt-6 text-center space-y-2">
            {authMode === 'sign_in' && (
              <>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  {t('auth.dontHaveAccount')}{' '}
                  <button
                    onClick={() => setAuthMode('sign_up')}
                    className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium underline"
                  >
                    {t('auth.register')}
                  </button>
                </p>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  <button
                    onClick={() => setAuthMode('forgot_password')}
                    className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium underline"
                  >
                    {t('auth.resetPassword')}
                  </button>
                </p>
              </>
            )}

            {authMode === 'sign_up' && (
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                {t('auth.alreadyHaveAccount')}{' '}
                <button
                  onClick={() => setAuthMode('sign_in')}
                  className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium underline"
                >
                  {t('auth.login')}
                </button>
              </p>
            )}

            {authMode === 'forgot_password' && (
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                <button
                  onClick={() => setAuthMode('sign_in')}
                  className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium underline"
                >
                  {t('auth.backToSignIn')}
                </button>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Terms of Service Modal */}
      <TermsOfServiceModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onApprove={() => {
          setHasReadTerms(true);
          setTermsAccepted(true);
        }}
      />
    </div>
  )
}

export default AuthComponent
